import { useEffect, useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { fetchAIStatus, queryAI } from '@/api/ai'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  addRecentQuestion,
  clearRecentQuestions,
  loadRecentQuestions,
} from '@/lib/aiRecentQuestions'
import { AiAnswer } from '@/components/ai/AiAnswer'
import { cn } from '@/lib/utils'

const SCOPE_LABELS = {
  general: 'estate data',
  threatdown_detections: 'ThreatDown detections',
}

export function AiQueryPanel({
  scope = 'general',
  customerIds = [],
  title = 'Ask AI',
  description,
  examples = [],
  className,
}) {
  const [status, setStatus] = useState(null)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [recentQuestions, setRecentQuestions] = useState([])

  useEffect(() => {
    fetchAIStatus().then(setStatus).catch(() => setStatus({ configured: false, enabled: false }))
  }, [])

  useEffect(() => {
    setRecentQuestions(loadRecentQuestions(scope))
  }, [scope])

  function rememberQuestion(text) {
    setRecentQuestions(addRecentQuestion(scope, text))
  }

  function handleClearRecent() {
    setRecentQuestions(clearRecentQuestions(scope))
  }

  async function handleAsk(event) {
    event.preventDefault()
    const trimmed = question.trim()
    if (!trimmed || loading) return

    rememberQuestion(trimmed)
    setLoading(true)
    setError('')
    setAnswer('')
    try {
      const result = await queryAI({ question: trimmed, scope }, customerIds)
      setAnswer(result.answer)
    } catch (err) {
      const detail = err.response?.data?.detail
      if (typeof detail === 'string' && detail.trim()) {
        setError(detail)
      } else if (Array.isArray(detail) && detail.length > 0) {
        setError(detail.map((item) => item.msg || JSON.stringify(item)).join('; '))
      } else if (err.message) {
        setError(err.message)
      } else {
        setError('Could not get an AI answer.')
      }
    } finally {
      setLoading(false)
    }
  }

  const ready = status?.enabled
  const scopeLabel = SCOPE_LABELS[scope] || scope

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-start gap-2">
          <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription>
              {description ||
                `Ask questions about your ${scopeLabel}. Answers use synced portal data only.`}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col space-y-4">
        {!status ? (
          <div className="h-20 rounded-lg bg-muted/30 animate-pulse" />
        ) : !ready ? (
          <p className="text-sm text-muted-foreground">
            AI assistant is not enabled. An administrator can configure a provider and API key in{' '}
            <span className="font-medium">Admin → Settings</span>.
          </p>
        ) : (
          <>
            {examples.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Suggested questions
                </p>
                <div className="flex flex-wrap gap-2">
                  {examples.map((example) => (
                    <button
                      key={example}
                      type="button"
                      className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground text-left"
                      onClick={() => setQuestion(example)}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {recentQuestions.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Recent questions
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={handleClearRecent}
                  >
                    Clear recent
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentQuestions.map((recent) => (
                    <button
                      key={recent}
                      type="button"
                      className="max-w-full rounded-md border border-primary/20 bg-primary/5 px-2 py-1 text-xs text-foreground hover:bg-primary/10 text-left"
                      onClick={() => setQuestion(recent)}
                      title={recent}
                    >
                      <span className="line-clamp-2">{recent}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleAsk} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor={`ai-question-${scope}`}>Your question</Label>
                <textarea
                  id={`ai-question-${scope}`}
                  rows={6}
                  className="flex min-h-[8rem] w-full flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Ask about counts, trends, or what something means…"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={loading || !question.trim()}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Thinking…
                  </>
                ) : (
                  'Ask'
                )}
              </Button>
            </form>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {answer && <AiAnswer answer={answer} />}
          </>
        )}
      </CardContent>
    </Card>
  )
}
